import { flattenGroups, selectFaqGroups } from './faqSelector';
import type { HelpContext, HelpFaq, HelpMessage } from './types';

export type ChatReply =
  | { kind: 'faq'; faq: HelpFaq; preface?: string }
  | { kind: 'fallback'; text: string; suggestions: HelpFaq[] };

/** Phase 1 is scripted; Phase 3 swaps in a backend-backed `answerFreeText`. */
export interface ChatEngine {
  answerFaq(faq: HelpFaq, ctx: HelpContext): Promise<string>;
  answerFreeText(text: string, ctx: HelpContext, history: HelpMessage[]): Promise<ChatReply>;
}

export const MATCH_PREFACE = "Here's what I found for that:";
export const FALLBACK_TEXT =
  "I don't have a scripted answer for that yet. Try one of the questions on the left, or reach out and the DVA team can pick it up from here.";

const STOPWORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'do', 'does', 'did', 'how', 'what',
  'why', 'when', 'where', 'which', 'who', 'can', 'could', 'should', 'would', 'will', 'i', 'my', 'me',
  'we', 'our', 'us', 'you', 'your', 'this', 'that', 'these', 'those', 'for', 'of', 'to', 'in', 'on',
  'it', 'its', 'with', 'and', 'or', 'about', 'if', 'there', 'here', 'have', 'has', 'get', 'not',
  'dva', 'use', 'case', 'cases',
]);

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9%\s-]/g, ' ')
    .split(/\s+/)
    .map((token) => (token.length > 3 && token.endsWith('s') ? token.slice(0, -1) : token))
    .filter((token) => token.length >= 2 && !STOPWORDS.has(token));
}

export interface FaqMatch {
  faq: HelpFaq;
  score: number;
  overlap: number;
}

/** Simple token overlap on question + tags. `candidates` must already be in priority order. */
export function matchFaq(text: string, candidates: HelpFaq[]): FaqMatch | null {
  const queryTokens = Array.from(new Set(tokenize(text)));
  if (queryTokens.length === 0) return null;

  let best: FaqMatch | null = null;
  for (const faq of candidates) {
    const faqTokens = new Set([...tokenize(faq.question), ...faq.tags.flatMap(tokenize)]);
    const overlap = queryTokens.filter((token) => faqTokens.has(token)).length;
    if (overlap === 0) continue;
    const score = overlap / queryTokens.length;
    if (!best || score > best.score || (score === best.score && overlap > best.overlap)) {
      best = { faq, score, overlap };
    }
  }
  if (!best) return null;

  const confident = best.overlap >= 2 ? best.score >= 0.4 : queryTokens.length <= 2;
  return confident ? best : null;
}

export interface ScriptedEngineOptions {
  faqs: HelpFaq[];
  contexts: HelpContext[];
  includeDrafts?: boolean;
  suggestionCount?: number;
}

export function createScriptedEngine({ faqs, contexts, includeDrafts, suggestionCount = 2 }: ScriptedEngineOptions): ChatEngine {
  const candidatesFor = (ctx: HelpContext) => flattenGroups(selectFaqGroups(ctx.key, faqs, contexts, { includeDrafts }));

  return {
    async answerFaq(faq) {
      return faq.answer_md;
    },
    async answerFreeText(text, ctx) {
      const candidates = candidatesFor(ctx);
      const match = matchFaq(text, candidates);
      if (match) return { kind: 'faq', faq: match.faq, preface: MATCH_PREFACE };
      return { kind: 'fallback', text: FALLBACK_TEXT, suggestions: candidates.slice(0, suggestionCount) };
    },
  };
}
